
import React, { Component, ErrorInfo, ReactNode } from "react";
import { logError } from "@/lib/error-handler";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  componentStack: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    componentStack: null
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logError(error, 'ErrorBoundary');
    console.error("Component stack:", errorInfo.componentStack);
    
    // Store component stack for display
    this.setState({
      errorInfo: errorInfo,
      componentStack: errorInfo.componentStack
    });
    
    // Log additional context
    console.error("Error context:", {
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      errorMessage: error.message
    });
  }

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <div className="p-6 bg-card rounded-lg shadow-md max-w-lg w-full">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="h-6 w-6 text-destructive" />
              <h1 className="text-xl font-bold">Something went wrong</h1>
            </div>
            <p className="mb-4 text-muted-foreground">
              {this.state.error?.message || "An unexpected error occurred"}
            </p>
            {this.state.error?.stack && (
              <details className="mb-4">
                <summary className="text-sm text-blue-500 cursor-pointer">Technical details</summary>
                <pre className="mt-2 p-2 bg-slate-100 dark:bg-slate-800 rounded text-xs overflow-auto max-h-40">
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            <div className="flex gap-4">
              <button 
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Reload page
              </button>
              <button 
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90"
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
